import { useEffect } from 'react';

function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  } catch {
    /* ignore */
  }
  // iOS Safari legacy Home Screen flag
  return Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

/** Read CSS viewport unit height (e.g. 100lvh) via a probe element. */
function probeCssHeight(unit: string): number {
  if (typeof document === 'undefined') return 0;
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = `position:fixed;left:0;top:0;width:0;height:${unit};visibility:hidden;pointer-events:none;`;
  document.documentElement.appendChild(el);
  const h = el.getBoundingClientRect().height;
  el.remove();
  return Number.isFinite(h) ? h : 0;
}

/** Keeps --app-height in sync (iOS Safari / Home Screen PWA). */
export function useAppViewport() {
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => {
      const standalone = isStandaloneDisplay();
      root.classList.toggle('pwa-standalone', standalone);

      // Chat route shell owns viewport vars and keeps the outer shell at layout height.
      if (root.classList.contains('chat-route-active')) {
        root.style.setProperty('--app-height', `${Math.round(window.innerHeight)}px`);
        return;
      }

      const vv = window.visualViewport;
      const vvH = vv?.height ?? window.innerHeight;
      const layoutH = window.innerHeight;
      const offsetTop = vv?.offsetTop ?? 0;
      const lvh = probeCssHeight('100lvh') || probeCssHeight('100vh') || layoutH;
      /* Tab bar CSS uses env(safe-area-inset-bottom) directly — keep vars clean. */
      root.style.removeProperty('--safe-bottom-measured');
      root.style.setProperty('--footer-gap', '0px');

      if (standalone) {
        const keyboardLikely = vvH + offsetTop < layoutH - 40;
        if (keyboardLikely) {
          root.style.setProperty('--app-height', `${Math.round(vvH)}px`);
        } else {
          root.style.setProperty(
            '--app-height',
            `${Math.round(Math.max(layoutH, lvh, vvH + offsetTop))}px`,
          );
        }
        return;
      }

      root.style.setProperty('--app-height', `${Math.round(vvH)}px`);
    };

    sync();
    requestAnimationFrame(sync);

    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);

    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
      root.style.removeProperty('--app-height');
      root.style.removeProperty('--footer-gap');
      root.style.removeProperty('--safe-bottom-measured');
      root.classList.remove('pwa-standalone');
    };
  }, []);
}
